import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { ShoppingBag, Menu, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";

const links = [
  { to: "/collection", label: "Collection" },
  { to: "/contact", label: "Contact" },
];

const CurrencySwitch = () => {
  const { currency, setCurrency, currencies } = useCurrency();
  const codes = Object.keys(currencies || {});
  if (codes.length < 2) return null;
  return (
    <div data-testid="currency-switch" className="flex items-center gap-1 font-mono-label text-[10px] uppercase tracking-luxe">
      {codes.map((c, i) => (
        <span key={c} className="flex items-center">
          {i > 0 && <span className="text-white/20 mx-1">/</span>}
          <button
            type="button"
            data-testid={`currency-${c}`}
            aria-pressed={currency === c}
            onClick={() => setCurrency(c)}
            className={`px-1.5 -mx-1.5 py-3 -my-3 touch-manipulation transition-colors ${currency === c ? "text-white" : "text-white/40 hover:text-white/70"}`}
          >
            {c.toUpperCase()}
          </button>
        </span>
      ))}
    </div>
  );
};

export const Navbar = () => {
  const { count, setOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const onHome = pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const solid = scrolled || !onHome;

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        solid ? "bg-black/70 backdrop-blur-xl backdrop-saturate-150 border-b border-white/10" : "bg-transparent"
      }`}
    >
      <nav className="max-w-[1600px] mx-auto px-6 lg:px-12 h-16 lg:h-20 flex items-center justify-between">
        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden text-white"
          onClick={() => setMobileOpen((v) => !v)}
        >
          {mobileOpen ? <X strokeWidth={1.3} /> : <Menu strokeWidth={1.3} />}
        </button>

        <div className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className="text-xs font-mono-label uppercase tracking-luxe text-white/70 hover:text-white transition-colors"
            >
              {l.label}
            </Link>
          ))}
        </div>

        <Link
          to="/"
          data-testid="nav-logo"
          className="absolute left-1/2 -translate-x-1/2 font-serif-display text-xl lg:text-2xl tracking-[0.2em] text-white uppercase"
        >
          Ignota Pars
        </Link>

        <div className="relative z-10 flex items-center gap-6">
          <CurrencySwitch />
          <button
            data-testid="cart-open-btn"
            onClick={() => setOpen(true)}
            className="relative text-white"
            aria-label="Open cart"
          >
            <ShoppingBag strokeWidth={1.3} size={20} />
            {count > 0 && (
              <span
                data-testid="cart-count"
                className="absolute -top-2 -right-2 bg-white text-black text-[10px] font-mono-label w-4 h-4 flex items-center justify-center"
              >
                {count}
              </span>
            )}
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div className="md:hidden bg-black border-b border-white/10 px-6 py-6 flex flex-col gap-5">
          {links.map((l) => (
            <button
              key={l.to}
              data-testid={`nav-mobile-${l.label.toLowerCase()}`}
              onClick={() => {
                navigate(l.to);
                setMobileOpen(false);
              }}
              className="text-left text-sm font-mono-label uppercase tracking-luxe text-white/80"
            >
              {l.label}
            </button>
          ))}
        </div>
      )}
    </header>
  );
};
